import { useCallback, useEffect, useRef, useState } from "react";
import type { VoiceAgentService } from "@/services/integrations/elevenlabs/VoiceAgentService";

const NOISE_THRESHOLDS = {
  vadAvgMin: 0.45,
  vadHighFraction: 0.6,
  vadHighCutoff: 0.6,
  rmsMin: 0.05,
  agentSilenceMs: 8000,
  sessionWarmupMs: 6000,
  triggerHoldMs: 4000,
  cooldownMs: 60_000,
  maxTriggersPerSession: 3,
  recoveryAvgVad: 0.25,
  recoveryTicks: 120,
  vadWindowMax: 200,
  rmsTickMs: 250,
  rmsWindowMax: 20,
} as const;

type UseNoiseEnvironmentOptions = {
  getService: () => VoiceAgentService | null;
  enabled: boolean;
  isAgentSpeaking: boolean;
};

function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function fractionAbove(values: number[], cutoff: number): number {
  if (values.length === 0) return 0;
  return values.filter((x) => x > cutoff).length / values.length;
}

export const useNoiseEnvironment = ({ getService, enabled, isAgentSpeaking }: UseNoiseEnvironmentOptions) => {
  const [isNoisy, setIsNoisy] = useState(false);
  const vadBuf = useRef<number[]>([]);
  const rmsBuf = useRef<number[]>([]);
  const suspectSinceRef = useRef<number | null>(null);
  const lastDismissedRef = useRef(0);
  const lastAgentSpeakRef = useRef(0);
  const sessionStartRef = useRef(0);
  const triggerCountRef = useRef(0);
  const recoveryStreakRef = useRef(0);
  const isNoisyRef = useRef(false);

  useEffect(() => {
    isNoisyRef.current = isNoisy;
  }, [isNoisy]);

  useEffect(() => {
    if (isAgentSpeaking) {
      lastAgentSpeakRef.current = Date.now();
    }
  }, [isAgentSpeaking]);

  const dismiss = useCallback(() => {
    lastDismissedRef.current = Date.now();
    suspectSinceRef.current = null;
    recoveryStreakRef.current = 0;
    isNoisyRef.current = false;
    setIsNoisy(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      vadBuf.current = [];
      rmsBuf.current = [];
      suspectSinceRef.current = null;
      recoveryStreakRef.current = 0;
      isNoisyRef.current = false;
      queueMicrotask(() => {
        setIsNoisy(false);
      });
      return;
    }

    const service = getService();
    if (!service) {
      return;
    }

    sessionStartRef.current = Date.now();
    lastAgentSpeakRef.current = Date.now();

    service.onVadScore((score) => {
      vadBuf.current.push(score);
      if (vadBuf.current.length > NOISE_THRESHOLDS.vadWindowMax) {
        vadBuf.current.shift();
      }
    });

    const tick = () => {
      const svc = getService();
      if (!svc) return;

      const rms = svc.getInputVolume();
      rmsBuf.current.push(rms);
      if (rmsBuf.current.length > NOISE_THRESHOLDS.rmsWindowMax) {
        rmsBuf.current.shift();
      }

      const now = Date.now();

      if (now - sessionStartRef.current < NOISE_THRESHOLDS.sessionWarmupMs) {
        return;
      }

      if (now - lastDismissedRef.current < NOISE_THRESHOLDS.cooldownMs && lastDismissedRef.current > 0) {
        suspectSinceRef.current = null;
        return;
      }

      if (now - lastAgentSpeakRef.current < NOISE_THRESHOLDS.agentSilenceMs) {
        suspectSinceRef.current = null;
        recoveryStreakRef.current = 0;
        return;
      }

      const vad = vadBuf.current;
      if (vad.length < 20) {
        return;
      }

      const avgVad = average(vad);
      const pctHigh = fractionAbove(vad, NOISE_THRESHOLDS.vadHighCutoff);
      const avgRms = average(rmsBuf.current);

      if (isNoisyRef.current && avgVad < NOISE_THRESHOLDS.recoveryAvgVad) {
        recoveryStreakRef.current += 1;
        if (recoveryStreakRef.current >= NOISE_THRESHOLDS.recoveryTicks) {
          isNoisyRef.current = false;
          setIsNoisy(false);
          recoveryStreakRef.current = 0;
          suspectSinceRef.current = null;
        }
      } else {
        recoveryStreakRef.current = 0;
      }

      const hot =
        avgVad > NOISE_THRESHOLDS.vadAvgMin &&
        pctHigh > NOISE_THRESHOLDS.vadHighFraction &&
        avgRms > NOISE_THRESHOLDS.rmsMin;

      if (!hot) {
        suspectSinceRef.current = null;
        return;
      }

      if (isNoisyRef.current) {
        return;
      }

      if (triggerCountRef.current >= NOISE_THRESHOLDS.maxTriggersPerSession) {
        return;
      }

      if (suspectSinceRef.current === null) {
        suspectSinceRef.current = now;
      }
      if (now - suspectSinceRef.current < NOISE_THRESHOLDS.triggerHoldMs) {
        return;
      }

      triggerCountRef.current += 1;
      isNoisyRef.current = true;
      setIsNoisy(true);
    };

    const id = window.setInterval(tick, NOISE_THRESHOLDS.rmsTickMs);

    return () => {
      window.clearInterval(id);
      service.onVadScore(null);
      vadBuf.current = [];
      rmsBuf.current = [];
    };
  }, [enabled, getService]);

  return { isNoisy, dismiss };
};
